
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CoinsIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { format } from "date-fns";

interface TotalStakeProps {
  value: number;
  isLoading: boolean;
}

export function TotalStake({ value, isLoading }: TotalStakeProps) {
  const formatStakeInMillions = (stake: number): string => {
    return `${(stake / 1_000_000).toFixed(2)}M DUSK`;
  };

  // Fetch current DUSK price
  const { data: marketData } = useQuery({
    queryKey: ['marketData'],
    queryFn: async () => {
      const { data } = await supabase
        .from('dusk_market_data')
        .select('current_price_usd')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();
      return data;
    }
  });

  // Fetch historical stake data with NO LIMIT to ensure we get all available data
  const { data: historicalData, isLoading: isHistoricalLoading, error } = useQuery({
    queryKey: ['historicalStake'],
    queryFn: async () => {
      console.log("Fetching historical stake data");
      
      // Get ALL available daily data without limits
      const { data: dailyData, error: dailyError } = await supabase
        .from('daily_aggregated_stats')
        .select('date, total_stake')
        .order('date', { ascending: true });
      
      if (dailyError) {
        console.error("Error fetching daily data:", dailyError);
        throw dailyError;
      }
      
      console.log("Daily data fetched:", dailyData?.length || 0, "records");
      console.log("First record:", dailyData?.[0]);
      console.log("Last record:", dailyData?.[dailyData?.length - 1]);

      // Get the last daily value
      const { data: lastDaily } = await supabase
        .from('daily_aggregated_stats')
        .select('total_stake')
        .order('date', { ascending: false })
        .limit(1)
        .single();

      if (!dailyData || dailyData.length === 0) {
        console.log("No daily data available");
        return { historicalData: [], lastDailyValue: 0 };
      }

      // Process data for chart
      const chartData = dailyData.map(item => {
        const date = new Date(item.date);
        return {
          rawDate: item.date,
          date: date,
          displayDate: format(date, 'MMM dd'),
          value: Number(item.total_stake || 0)
        };
      });
      
      console.log("Processed chart data:", chartData.length, "records");
      console.log("First processed:", chartData[0]);
      console.log("Last processed:", chartData[chartData.length - 1]);

      return {
        historicalData: chartData,
        lastDailyValue: lastDaily?.total_stake || 0
      };
    }
  });

  if (error) {
    console.error("Error in historical data query:", error);
  }

  const duskPrice = marketData?.current_price_usd || 0;
  const usdValue = value * duskPrice;

  // Calculate the difference in millions
  const stakeDifference = historicalData?.lastDailyValue 
    ? (value - historicalData.lastDailyValue) / 1_000_000
    : 0;

  const formattedDifference = stakeDifference !== 0 
    ? `${stakeDifference > 0 ? '+' : ''}${stakeDifference.toFixed(1)}M`
    : null;

  const formatYAxis = (value: number) => {
    return `${(value / 1_000_000).toFixed(1)}M`;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Total Network Stake</CardTitle>
        <CoinsIcon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="flex flex-col space-y-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">
                {isLoading ? "Loading..." : formatStakeInMillions(value)}
              </span>
              {formattedDifference && (
                <span className={`text-sm ${stakeDifference > 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {formattedDifference}
                </span>
              )}
            </div>
            {usdValue > 0 && (
              <div className="text-xs text-yellow-500 font-medium">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  notation: 'compact',
                  maximumFractionDigits: 0
                }).format(usdValue)}
              </div>
            )}
          </div>
          
          <div className="h-[120px] mt-4">
            {!isHistoricalLoading && historicalData?.historicalData && historicalData.historicalData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={historicalData.historicalData}
                  margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                >
                  <XAxis
                    dataKey="displayDate"
                    stroke="#888888"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    tickMargin={5}
                  />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatYAxis}
                    width={45}
                    tickCount={5}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="rounded-lg border bg-background p-2 shadow-sm">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex flex-col">
                                <span className="text-[0.70rem] uppercase text-muted-foreground">
                                  Date
                                </span>
                                <span className="font-bold text-sm">
                                  {data.displayDate}
                                </span>
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[0.70rem] uppercase text-muted-foreground">
                                  Total Stake
                                </span>
                                <span className="font-bold text-sm">
                                  {formatStakeInMillions(Number(payload[0].value))}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#6e46ae"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                {isHistoricalLoading ? "Loading data..." : error ? "Error loading data" : "No historical data available"}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
